import { Contract } from "ethers";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useAccount, useNetwork } from "wagmi";
import { Box } from "../../components/Layout/Box";
import { Content } from "../../components/Layout/Content";
import { BootyABI } from "../../constants/ABI/BootyABI";
import { BOOTY_ADDRESS, OLD_BOOTY_ADDRESS } from "../../constants/addresses";
import { Inventory } from "./Inventory";
import { crates, artifacts, itemsMap } from "./Items";
import { Crafting } from "./Crafting";
import { LogoLoader } from "../../components/Loaders";
import { Media, breakpoints } from "../../styles";
import { ButtonSecondary } from "@/components/Buttons/Button";
import { BackArrowIcon } from "@/components/Icons/BackArrowIcon";
import { useNavigationHelpers } from "@/hooks/navigation";
import { ConfirmMigrate } from "./ConfirmMigrate";
import { useWidthBreakpoint } from "@/hooks";
import { CONNECTED_CHAIN } from "@/constants/chains";
import { useEthersProvider } from '@/utils/wagmi-ethers-adapters/ethers';

interface TabGroupProps {
  views: string[];
  activeView: string;
  setActiveView: (activeView: string) => void;
}

function TabGroup({ views, activeView, setActiveView }: TabGroupProps) {
  return (
    <ButtonGroup>
      {views.map((view) => (
        <Tab
          key={view}
          activeView={activeView === view}
          onClick={() => setActiveView(view)}
        >
          {view}
        </Tab>
      ))}
    </ButtonGroup>
  );
}

export const crateIDs = new Set(crates.map(crate => crate.crateID))

export const BootyScreen = () => {
  const { handleGoBack } = useNavigationHelpers();

  const { address: walletAddress, isConnected } = useAccount();
  const provider = useEthersProvider();

  const { chain: activeChain } = useNetwork();
  const validChain = activeChain?.name == CONNECTED_CHAIN
  const booty = new Contract(BOOTY_ADDRESS, BootyABI, provider);

  const [balanceLoading, setBalanceLoading] = useState(false);
  const [recipeLoading, setRecipeLoading] = useState(false);

  const [oldBalances, setOldBalances] = useState<number[]>([]);
  const [crateBalances, setCrateBalances] = useState<number[]>([]);
  const [artifactBalances, setArtifactBalances] = useState<any[]>([]);
  const [inventoryBalances, setInventoryBalances] = useState<any[]>([]);
  const [recipes, setRecipes] = useState<Record<string, any>>({})

  const views = ["Inventory", "Crafting"];
  const [activeView, setActiveView] = useState(views[0]);

  const [migrateAmounts, setMigrateAmounts] = useState<Record<string, string>>({});
  const [confirmMigrateVisible, setConfirmMigrateVisible] = useState(false)

  const isMobile = useWidthBreakpoint(breakpoints.mobile)

  const getOldBalances = async () => {
    const oldBooty = new Contract(OLD_BOOTY_ADDRESS, BootyABI, provider);
    const numItems = 21 // 3 crates and 18 artifacts in old contract
    const inventoryIDs = Array.from(Array(numItems).keys());
    const balances = await oldBooty.balanceOfBatch(
      inventoryIDs.map((_) => walletAddress),
      inventoryIDs
    );
    setOldBalances(balances)
  }

  const updateBalances = async () => {
    const inventoryIDs = itemsMap.map((_, index) => index);
    const balances = await booty.balanceOfBatch(
      inventoryIDs.map((_) => walletAddress),
      inventoryIDs
    );

    setCrateBalances(Array.from(crateIDs).map(crateID => balances[crateID]));

    const sortedArtifacts : any[] = []

    balances.forEach((balance: number, index: number) => {
        if(!crateIDs.has(index)){
            sortedArtifacts.push({ artifact: itemsMap[index], balance: balance })
        }
    })
    
    setArtifactBalances([...sortedArtifacts]);

    const copy = [...sortedArtifacts];
    const newItems = copy.splice(18).reverse(); //Move Countdown event items to front
    copy.unshift(...newItems);
    setInventoryBalances(copy);
  };

  const getRecipes = async () => {

    const newRecipes : Record<string, any> = {};

    let i = 0;
    while(true){
        try{
            const [active, inputIDs, inputAmounts, outputIDs, outputAmounts] = await booty.getRecipe(i);
            if(active){
                newRecipes[i.toString()] = {
                    inputs: inputIDs.map((id: number) => itemsMap[id]),
                    inputAmounts: inputAmounts.map((amt: any) => amt.toNumber()),
                    outputs: outputIDs.map((id: number) => itemsMap[id]),
                    outputAmounts: outputAmounts.map((amt: any) => amt.toNumber())
                }
            }
            i++;
        } catch {
            setRecipes(newRecipes);        
            break
        }
    }

  };

  useEffect(() => {
    if (isConnected && validChain) {
      setBalanceLoading(true);
      getOldBalances();
      updateBalances().then(() => setBalanceLoading(false));
    } else {
      setOldBalances([])
      setCrateBalances([]);
      const zeroBalances: any[] = artifacts.map((artifact) => {
        return { artifact: artifact, balance: 0 };
      });
      setArtifactBalances([...zeroBalances]);
      const copy = [...zeroBalances];
      const newItems = copy.splice(18).reverse(); //Move Countdown event items to front
      copy.unshift(...newItems);
      setInventoryBalances(copy);
    }
  }, [walletAddress, validChain]);

  useEffect(() => {
    setRecipeLoading(true);
    getRecipes().then(() => setRecipeLoading(false));
  }, []);

  useEffect(() => {
    const newMigrateAmounts : Record<string, string> = {}
    oldBalances.forEach((balance, index) => {
        if(balance > 0){
            newMigrateAmounts[index.toString()] = balance.toString()
        }
    })
    setMigrateAmounts(newMigrateAmounts)
  }, [oldBalances])

  return (
    <Content>
      {balanceLoading || recipeLoading ? (
        <LoadingWrapper>
          <LogoLoader />
        </LoadingWrapper>
      ) : (
        <View>
          <BootyHeader>
            <BootyTitle onClick={() => handleGoBack("/points")}>
              <BackArrowIcon />
              Accountant's Trove
            </BootyTitle>
            <a href={'https://opensea.io/collection/accountants-trove'} target="_blank">
              <BootyButton>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_3698_26403)">
                    <path
                      d="M24 12C24 18.627 18.627 24 12 24C5.37295 24 0 18.627 0 12C0 5.37295 5.37295 0 12 0C18.6284 0 24 5.37295 24 12Z"
                      fill="#2081E2"
                    />
                    <path
                      d="M5.9234 12.403L5.97516 12.3216L9.09684 7.4381C9.14247 7.36661 9.24972 7.374 9.28424 7.45166C9.80576 8.62044 10.2558 10.074 10.0449 10.979C9.95494 11.3513 9.70836 11.8555 9.43095 12.3216C9.39521 12.3894 9.35576 12.456 9.31383 12.5201C9.2941 12.5497 9.26081 12.5669 9.22505 12.5669H6.01462C5.92832 12.5669 5.87777 12.4732 5.9234 12.403Z"
                      fill="white"
                    />
                    <path
                      d="M19.8337 13.3114V14.0844C19.8337 14.1288 19.8066 14.1683 19.7671 14.1855C19.5255 14.2891 18.6982 14.6688 18.3543 15.1472C17.4764 16.369 16.8058 18.116 15.3066 18.116H9.0521C6.83539 18.116 5.03906 16.3135 5.03906 14.0894V14.0179C5.03906 13.9587 5.08713 13.9106 5.14632 13.9106H8.63294C8.70197 13.9106 8.75251 13.9747 8.74637 14.0425C8.72169 14.2694 8.76362 14.5011 8.87088 14.712C9.078 15.1324 9.50705 15.395 9.97061 15.395H11.6967V14.0475H9.99033C9.90281 14.0475 9.85104 13.9463 9.90158 13.8748C9.92006 13.8465 9.94104 13.8169 9.96321 13.7836C10.1247 13.5543 10.3553 13.198 10.5846 12.7924C10.7412 12.5187 10.8928 12.2265 11.0149 11.9331C11.0395 11.88 11.0592 11.8258 11.079 11.7728C11.1123 11.6791 11.1468 11.5915 11.1714 11.504C11.1961 11.43 11.2158 11.3523 11.2356 11.2796C11.2935 11.0306 11.3182 10.7667 11.3182 10.493C11.3182 10.3858 11.3132 10.2736 11.3034 10.1663C11.2984 10.0492 11.2836 9.93205 11.2688 9.81493C11.259 9.71137 11.2405 9.60904 11.2208 9.50178C11.1961 9.34521 11.1616 9.18988 11.1221 9.03328L11.1086 8.97412C11.079 8.86684 11.0543 8.76452 11.0198 8.65727C10.9224 8.32067 10.8102 7.99273 10.6919 7.68575C10.6487 7.56368 10.5994 7.44657 10.5501 7.32945C10.4773 7.15314 10.4034 6.99287 10.3356 6.84121C10.301 6.77217 10.2714 6.70929 10.2419 6.64518C10.2086 6.57244 10.1741 6.49969 10.1395 6.43067C10.1149 6.37765 10.0865 6.32833 10.0668 6.27901L9.85596 5.88942C9.82637 5.83641 9.87569 5.77353 9.93362 5.78956L11.2528 6.14709H11.2565C11.259 6.14709 11.2602 6.14833 11.2614 6.14833L11.4353 6.19641L11.6264 6.25067L11.6967 6.27037V5.48627C11.6967 5.10777 12 4.80078 12.3748 4.80078C12.5622 4.80078 12.7323 4.87722 12.8544 5.00173C12.9764 5.12627 13.0528 5.29641 13.0528 5.48627V6.65013L13.1934 6.68956C13.2045 6.69328 13.2156 6.6982 13.2254 6.70559C13.26 6.73149 13.3093 6.76969 13.3722 6.81657C13.4215 6.856 13.4745 6.90409 13.5386 6.95341C13.6656 7.05573 13.8172 7.18765 13.9837 7.33931C14.028 7.37752 14.0712 7.41697 14.1107 7.45643C14.3252 7.65616 14.5656 7.8904 14.7949 8.14931C14.859 8.22205 14.9219 8.29602 14.986 8.37369C15.0501 8.4526 15.1179 8.53026 15.1771 8.60795C15.2548 8.71151 15.3386 8.81876 15.4114 8.93096C15.4459 8.98398 15.4853 9.03822 15.5186 9.09124C15.6123 9.23301 15.6949 9.37974 15.7738 9.52645C15.8071 9.59425 15.8416 9.66822 15.8712 9.74096C15.9588 9.937 16.0278 10.1367 16.0722 10.3365C16.0857 10.3796 16.0956 10.4265 16.1005 10.4684V10.4782C16.1153 10.5374 16.1203 10.6003 16.1252 10.6644C16.1449 10.8691 16.1351 11.0737 16.0907 11.2796C16.0722 11.3672 16.0475 11.4497 16.0179 11.5373C15.9883 11.6211 15.9588 11.7087 15.9205 11.7913C15.8466 11.9626 15.759 12.134 15.6555 12.2943C15.6222 12.3535 15.5827 12.4163 15.5433 12.4755C15.5001 12.5384 15.4557 12.5976 15.4163 12.6555C15.362 12.7295 15.3041 12.8072 15.2449 12.8762C15.1919 12.9489 15.1377 13.0217 15.0785 13.0858C14.9959 13.1832 14.917 13.2757 14.8344 13.3644C14.7851 13.4224 14.732 13.4816 14.6778 13.5346C14.6248 13.5937 14.5705 13.6467 14.5212 13.6961C14.4386 13.7787 14.3696 13.8428 14.3116 13.8958L14.176 14.0203C14.1563 14.0376 14.1304 14.0475 14.1033 14.0475H13.0528V15.395H14.3745C14.6704 15.395 14.9515 15.2902 15.1783 15.0979C15.256 15.0301 15.5951 14.7366 15.9957 14.294C16.0093 14.2792 16.0266 14.2681 16.0463 14.2632L19.6969 13.2079C19.7647 13.1881 19.8337 13.2399 19.8337 13.3114Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3698_26403">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                {!isMobile &&
                <>
                  Buy
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="arrow-icon"
                  >
                    <g clipPath="url(#clip0_3983_174)">
                      <path
                        d="M13.1727 11.9997L8.22266 7.04974L9.63666 5.63574L16.0007 11.9997L9.63666 18.3637L8.22266 16.9497L13.1727 11.9997Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3983_174">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </>
                }
              </BootyButton>
            </a>
          </BootyHeader>
          {Object.keys(migrateAmounts).length > 0 && 
           <>
             <MigrateHeader>
              <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                <img src={`../assets/seasons/season1.png`} style={{height: '48px'}}/>
                {isMobile ? "Migrate your BKB items" : "NOTICE: BKB is being upgraded to a new contract. Old items are now deprecated."}                
              </div>
              <BootyButton style={{marginLeft: '12px'}} onClick={() => setConfirmMigrateVisible(true)}>
                  {!isMobile && 'Migrate'} 
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="arrow-icon"
                  >
                    <g clipPath="url(#clip0_3983_174)">
                      <path
                        d="M13.1727 11.9997L8.22266 7.04974L9.63666 5.63574L16.0007 11.9997L9.63666 18.3637L8.22266 16.9497L13.1727 11.9997Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3983_174">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
              </BootyButton>
            </MigrateHeader>
            <ConfirmMigrate
              visible={confirmMigrateVisible}
              setVisible={setConfirmMigrateVisible}
              migrateAmounts={migrateAmounts}
              setMigrateAmounts={setMigrateAmounts}
              updateBalances={updateBalances}
            />
          </>
          }
          <TabGroup
            views={views}
            activeView={activeView}
            setActiveView={setActiveView}
          />
          {/* {
                        activeView == 'Hunt' && 
                        <CrateSale/>
                    } */}
          {activeView == "Inventory" && (
            <Inventory
              crateBalances={crateBalances}
              artifactBalances={inventoryBalances}
              updateBalances={updateBalances}
            />
          )}
          {activeView == "Crafting" && (
            <Crafting
              recipesMap={recipes}
              artifactBalances={artifactBalances.map((e) => e.balance)}
              updateBalances={updateBalances}
            />
          )}
        </View>
      )}
    </Content>
  );
};

const BootyButton = styled(ButtonSecondary)`
  width: fit-content;
  height: 48px;
  border-width: 2px;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  padding-inline: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 16px;

  .arrow-icon {
    transition: all 0.3s;
  }

  &:hover {
    .arrow-icon {
      transform: translateX(5px);
    }
  }

  ${Media.mobile}{
    padding-inline: 12px;
  }
`;

const View = styled(Box)`
  padding: 20px 20px 20px 20px;

  ${Media.tablet} {
    padding: 16px;
  }
`;

const BootyHeader = styled.div`
  margin-bottom: 24px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BootyTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-weight: 500;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: -0.03em;
  color: #ffffff;
  cursor: pointer;

  ${Media.tablet} {
    font-size: 20px;
    gap: 8px;
  }
`;

const MigrateHeader = styled.div`
  display: flex;
  height: fit-content;
  margin-bottom: 12px;
  padding: 12px;
  background: #151530;
  border-radius: 16px;
  border: 1px solid #1E2239;
  align-items: center;
  justify-content: space-between;

  ${Media.mobile}{
    font-size: 14px;
  }
`

const Tab = styled.button<{ activeView: boolean }>`
  font-size: 20px;
  width: 200px;
  font-weight: 1;
  padding-bottom: 10px;
  cursor: pointer;
  opacity: 0.6;
  background: transparent;
  color: white;
  border: 0;
  outline: 0;
  ${({ activeView }) =>
    activeView &&
    `
    border-bottom: 2px solid #00BDFF;
    opacity: 1;
    font-weight: 500;
    `}
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  margin-bottom: 20px;
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  vertical-align: center;
  width: 100%;
  padding: 300px 0 300px 0;
`;

const Image = styled.img`
  width: 20px;
  height: 20px;
`;

const ArbitrumIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #37dcf2;
  border-radius: 12px;
`;
